/**
 * Ciari Product Slider
 * Swiper handles carousel interaction; CSS handles the visual fan.
 *
 * Color swatches use the existing Liquid markup without requiring Liquid edits.
 * On selection, this script fetches Shopify's public product JSON, finds the
 * variant matching the swatch label, and updates the corresponding slider image.
 */
(function () {
  'use strict';

  class CiariProductSlider extends window.CiariCarousel.Element {
    init() {
      if (this.swiper || !window.Swiper || !this.isConnected) return;

      this.track = this.querySelector('.cps__track');
      this.metaItems = Array.from(this.querySelectorAll('.cps__meta-item'));
      if (!this.track) return;

      this.productDataCache = new Map();
      this.track.querySelectorAll('.cps__slide').forEach((slide) => {
        const index = slide.dataset.index;
        const img = slide.querySelector('.cps__slide-img');
        if (index != null && img && !img.dataset.cpsOriginalSrc) {
          img.dataset.cpsOriginalSrc = img.currentSrc || img.src;
          if (img.srcset) img.dataset.cpsOriginalSrcset = img.srcset;
        }
      });

      this.onClick = this.onClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.initializeSwatches();

      var slides = this.querySelectorAll('.cps__slide');
      var count = slides.length;

      if (count < 2) {
        this.dataset.static = '';
        if (slides[0]) slides[0].dataset.pos = '0';
        this.addInteractionListeners();
        return;
      }

      var canLoop = count >= 6;
      this.mountCarousel(this.track, {
        slidesPerView: 'auto',
        centeredSlides: true,
        initialSlide: Math.min(2, count - 1),
        watchSlidesProgress: true,
        spaceBetween: this.gap(),
        speed: this.motionSpeed(600),
        slideToClickedSlide: true,
        loop: canLoop,
        loopAdditionalSlides: canLoop ? 3 : 0,
        rewind: !canLoop,
        grabCursor: true,
        navigation: {
          prevEl: this.querySelector('.cps__nav-btn--prev'),
          nextEl: this.querySelector('.cps__nav-btn--next')
        },
        keyboard: { enabled: true, onlyInViewport: true },
        a11y: { enabled: true },
        autoplay: this.autoplayConfig(5000),
        on: {
          init: (swiper) => this.sync(swiper),
          progress: (swiper) => this.sync(swiper),
          slideChange: (swiper) => this.sync(swiper),
          resize: (swiper) => {
            var gap = this.gap();
            if (gap !== swiper.params.spaceBetween) {
              swiper.params.spaceBetween = gap;
              swiper.update();
            }
          }
        }
      });

      this.addInteractionListeners();
    }

    addInteractionListeners() {
      this.addEventListener('click', this.onClick, true);
      this.addEventListener('keydown', this.onKeyDown);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
    }

    initializeSwatches() {
      this.querySelectorAll('.cps__meta-item .cps__swatch').forEach(function (swatch, index) {
        swatch.setAttribute('role', 'button');
        swatch.setAttribute('tabindex', '0');
        swatch.setAttribute(
          'aria-label',
          swatch.querySelector('.visually-hidden')?.textContent.trim() || ('Select color ' + (index + 1))
        );
        swatch.setAttribute('aria-pressed', String(swatch.classList.contains('is-selected')));
      });
    }

    gap() {
      var slide = this.querySelector('.cps__slide');
      if (!slide) return 0;
      var stride = parseFloat(getComputedStyle(this).getPropertyValue('--cps-stride'));
      if (!stride) return 0;
      return Math.round((stride - 1) * slide.offsetWidth);
    }

    sync(swiper) {
      swiper.slides.forEach(function (slide) {
        var depth = -Math.round(slide.progress);
        slide.dataset.pos = Math.max(-3, Math.min(3, depth));
      });

      var active = swiper.slides[swiper.activeIndex];
      if (!active) return;

      var index = active.dataset.index;
      this.metaItems.forEach(function (item) {
        item.toggleAttribute('data-active', item.dataset.index === index);
      });
    }

    getProductJsonForIndex(index) {
      const slide = Array.from(this.querySelectorAll('.cps__slide')).find((item) => item.dataset.index === String(index));
      if (!slide) return Promise.reject(new Error('Could not find product slide.'));

      const href = slide.getAttribute('href');
      if (!href) return Promise.reject(new Error('Product slide has no URL.'));

      const productUrl = new URL(href, window.location.origin);
      const match = productUrl.pathname.match(/\/products\/([^/]+)/);
      if (!match) return Promise.reject(new Error('Could not identify product handle from slide URL.'));

      const jsonUrl = '/products/' + encodeURIComponent(match[1]) + '.js';
      if (!this.productDataCache.has(jsonUrl)) {
        this.productDataCache.set(jsonUrl, fetch(jsonUrl, { credentials: 'same-origin' }).then((response) => {
          if (!response.ok) throw new Error('Shopify product JSON request failed: ' + response.status);
          return response.json();
        }));
      }
      return this.productDataCache.get(jsonUrl);
    }

    async selectSwatch(swatch) {
      const metaItem = swatch.closest('.cps__meta-item');
      if (!metaItem) return;

      metaItem.querySelectorAll('.cps__swatch').forEach(function (item) {
        const selected = item === swatch;
        item.classList.toggle('is-selected', selected);
        item.setAttribute('aria-pressed', String(selected));
      });

      const index = metaItem.dataset.index;
      const colorName = (swatch.querySelector('.visually-hidden')?.textContent || '').trim().toLocaleLowerCase();
      if (!colorName || index == null) return;

      try {
        const product = await this.getProductJsonForIndex(index);
        const variants = Array.isArray(product.variants) ? product.variants : [];

        // Match the displayed swatch label against any variant option value.
        const variant = variants.find((item) => [item.option1, item.option2, item.option3]
          .some((value) => String(value || '').trim().toLocaleLowerCase() === colorName));
        if (!variant || !variant.featured_image || !variant.featured_image.src) {
          console.warn('[Ciari Product Slider] No variant image found for swatch:', colorName, product.handle);
          return;
        }

        const imageUrl = variant.featured_image.src;
        const matchingSlides = this.querySelectorAll('.cps__slide[data-index="' + CSS.escape(String(index)) + '"]');
        matchingSlides.forEach((slide) => {
          const image = slide.querySelector('.cps__slide-img');
          if (!image) return;
          // Remove srcset so the browser doesn't keep choosing the old image.
          image.removeAttribute('srcset');
          image.src = imageUrl;
          image.alt = variant.featured_image.alt || product.title || image.alt;
        });
      } catch (error) {
        console.error('[Ciari Product Slider] Could not update swatch image:', error);
      }
    }

    onClick(event) {
      const swatch = event.target.closest('.cps__swatch');
      if (swatch && this.contains(swatch)) {
        event.preventDefault();
        event.stopPropagation();
        this.selectSwatch(swatch);
        return;
      }

      const slide = event.target.closest('.cps__slide');
      if (!slide) return;

      if (!this.swiper || !this.swiper.allowClick || !slide.classList.contains('swiper-slide-active')) {
        event.preventDefault();
      }
    }

    onKeyDown(event) {
      const swatch = event.target.closest('.cps__swatch');
      if (!swatch || !this.contains(swatch)) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.selectSwatch(swatch);
      }
    }

    onFocusIn() {
      if (this.swiper && this.swiper.autoplay) this.swiper.autoplay.pause();
    }

    onFocusOut() {
      if (this.swiper && this.swiper.autoplay) this.swiper.autoplay.resume();
    }

    destroy() {
      if (this.onClick) this.removeEventListener('click', this.onClick, true);
      if (this.onKeyDown) this.removeEventListener('keydown', this.onKeyDown);
      if (this.onFocusIn) this.removeEventListener('focusin', this.onFocusIn);
      if (this.onFocusOut) this.removeEventListener('focusout', this.onFocusOut);
      if (this.swiper) this.destroyCarousel();
    }
  }

  if (!customElements.get('ciari-product-slider')) {
    customElements.define('ciari-product-slider', CiariProductSlider);
  }
})();
